import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Welcome-kit gift cart sync.
 *
 * Keeps free gift line items in the cart consistent with the subscription
 * lines in it. Loaded site-wide (1-scripts.liquid) so gifts are also cleaned
 * up when the subscription is removed from the cart drawer on any page.
 *
 * Config comes entirely from the DOM rendered by `1-skio-plan-picker`:
 * - the picker root carries `data-gift-auto-add-enabled` (master switch,
 *   "Enable subscription gifts" theme setting)
 * - each `[data-plan-frame]` carries `data-plan-id` (selling plan ID)
 * - gift panels marked "Add to first order" carry `data-gift-auto-add`,
 *   and each gift with a product set renders `data-gift-variant`
 *
 * Reconcile rules:
 * - subscription line whose selling plan maps to gifts → those gift
 *   variants are kept in the cart (quantity 1, flagged with a hidden
 *   `_welcome_kit` line item property)
 * - no subscription lines at all → every `_welcome_kit` line is removed
 *   (runs on every page, no config needed)
 * - gift lines that stop matching the mapping are only removed when the
 *   mapping is known for every subscription line in the cart, so gifts
 *   added on another product's page are never clobbered
 *
 * Making the gifts free is the "Welcome Kit" automatic Buy X Get Y
 * discount (scoped to subscription purchases) — this script never touches
 * prices.
 */

const GIFT_PROPERTY = '_welcome_kit';
const EVENT_SOURCE = 'gift-cart-sync';

class GiftCartSync {
  /** @type {Promise<void>} serializes reconcile runs */
  #queue = Promise.resolve();

  /** @type {boolean} a cart event arrived while a run was in flight */
  #rerun = false;

  /** @type {boolean} */
  #running = false;

  constructor() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#schedule(), { once: true });
    } else {
      this.#schedule();
    }
  }

  /** @param {CartUpdateEvent} event */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source === EVENT_SOURCE) return;
    this.#schedule();
  };

  #schedule() {
    if (window.Shopify?.designMode) return;

    if (this.#running) {
      this.#rerun = true;
      return;
    }

    this.#queue = this.#queue.then(() => this.#run());
  }

  async #run() {
    this.#running = true;

    try {
      do {
        this.#rerun = false;
        await this.#reconcile();
      } while (this.#rerun);
    } catch (error) {
      console.warn('[gift-cart-sync]', error);
    } finally {
      this.#running = false;
    }
  }

  /**
   * Reads the selling plan → gift variants mapping from any enabled plan
   * picker on the page.
   *
   * Plan content is resolved the same way the picker distributes it (interval
   * days → frame → the frame's selling plan ID), so the mapping is correct
   * whether or not the picker has moved the panels into their frames yet.
   * @returns {{ plans: Map<number, number[]>, hasConfig: boolean }}
   */
  #readConfig() {
    const plans = new Map();
    let hasConfig = false;

    const pickers = document.querySelectorAll('one-plan-picker-component[data-gift-auto-add-enabled]');
    for (const picker of pickers) {
      for (const frame of picker.querySelectorAll('[data-plan-frame]')) {
        const planId = Number(frame.dataset.planId);
        if (planId) {
          plans.set(planId, []);
          hasConfig = true;
        }
      }

      for (const content of picker.querySelectorAll('[data-plan-content]')) {
        if (!content.dataset.planDays) continue;
        const frame = picker.querySelector(`[data-plan-frame][data-interval-days="${content.dataset.planDays}"]`);
        const planId = Number(frame?.dataset.planId);
        if (!planId || !plans.has(planId)) continue;

        const variants = Array.from(content.querySelectorAll('[data-gift-auto-add] [data-gift-variant]'))
          .map((gift) => Number(gift.dataset.giftVariant))
          .filter(Boolean);

        plans.get(planId).push(...variants);
      }
    }

    return { plans, hasConfig };
  }

  async #reconcile() {
    const cart = await this.#fetchJson(`${Theme.routes.cart_url}.js`);
    const config = this.#readConfig();

    const subscriptionLines = cart.items.filter((item) => item.selling_plan_allocation);
    const giftLines = cart.items.filter((item) => item.properties?.[GIFT_PROPERTY]);

    if (!subscriptionLines.length && !giftLines.length) return;

    // Union of gift variants entitled by the subscription lines we can map.
    const desired = new Set();
    let allPlansKnown = true;
    for (const line of subscriptionLines) {
      const planId = line.selling_plan_allocation.selling_plan.id;
      const variants = config.plans.get(planId);
      if (variants) {
        for (const variantId of variants) desired.add(variantId);
      } else {
        allPlansKnown = false;
      }
    }

    // Unknown plans only block removals when the page has config at all;
    // the "no subscriptions → no gifts" guard always applies.
    const canRemoveUnmatched = subscriptionLines.length === 0 || (config.hasConfig && allPlansKnown);

    /** @type {Record<string, number>} */
    const updates = {};
    for (const line of giftLines) {
      if (desired.has(line.variant_id)) {
        if (line.quantity !== 1) updates[line.key] = 1;
      } else if (canRemoveUnmatched) {
        updates[line.key] = 0;
      }
    }

    const presentVariants = new Set(giftLines.map((line) => line.variant_id));
    const additions = Array.from(desired)
      .filter((variantId) => !presentVariants.has(variantId))
      .map((variantId) => ({
        id: variantId,
        quantity: 1,
        properties: { [GIFT_PROPERTY]: 'true' },
      }));

    if (!Object.keys(updates).length && !additions.length) return;

    if (Object.keys(updates).length) {
      await this.#fetchJson(`${Theme.routes.cart_update_url}.js`, { updates });
    }
    if (additions.length) {
      await this.#fetchJson(Theme.routes.cart_add_url, { items: additions });
    }

    const updatedCart = await this.#fetchJson(`${Theme.routes.cart_url}.js`);

    // The cart drawer auto-opens on any cart:update — a background sync
    // shouldn't pop it open, so suspend auto-open around the dispatch.
    const drawers = Array.from(document.querySelectorAll('cart-drawer-component[auto-open]'));
    for (const drawer of drawers) drawer.removeAttribute('auto-open');

    document.dispatchEvent(
      new CartUpdateEvent(updatedCart, EVENT_SOURCE, {
        source: EVENT_SOURCE,
        itemCount: updatedCart.item_count,
      })
    );

    requestAnimationFrame(() => {
      for (const drawer of drawers) drawer.setAttribute('auto-open', '');
    });
  }

  /**
   * @param {string} url
   * @param {object} [body] JSON body — POSTs when set, GETs otherwise
   */
  async #fetchJson(url, body) {
    const response = await fetch(url, body
      ? {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }
      : { headers: { Accept: 'application/json' } });

    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.description || json.message || `Cart request failed (${response.status})`);
    }
    return json;
  }
}

new GiftCartSync();
